class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

    // Delegated on document (not just the popup) so the same cross-sell
    // add-to-cart button works wherever it's rendered, e.g. the cart page
    // summary's below-minimum-order upsell, not only inside this popup.
    document.addEventListener('click', this.handleRecommendationAdd.bind(this));
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      const sectionHTML = parsedState.sections[section.id];
      if (!sectionHTML) return;

      const target = document.getElementById(section.id);
      const innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
      if (target && innerHTML !== undefined) target.innerHTML = innerHTML;
    });

    this.initRecommendationsInfiniteScroll();

    if (this.header) this.header.reveal();
    this.open();
  }

  // The recommendations carousel only ships its first 12 products (minus
  // anything already in the cart) synchronously; further pages are fetched
  // as the user scrolls the horizontal list toward its end, rather than
  // raising that initial cap.
  initRecommendationsInfiniteScroll() {
    const wrapper = document.querySelector('#cart-notification-recommendations .cart-notification-recommendations__inner');
    const list = wrapper ? wrapper.querySelector('[data-cnr-list]') : null;
    if (!wrapper || !list) return;

    this.recommendationsLoading = false;

    if (this.onRecommendationsScroll) {
      list.removeEventListener('scroll', this.onRecommendationsScroll);
    }
    this.onRecommendationsScroll = () => this.handleRecommendationsScroll(wrapper, list);
    list.addEventListener('scroll', this.onRecommendationsScroll, { passive: true });
  }

  handleRecommendationsScroll(wrapper, list) {
    if (this.recommendationsLoading) return;

    const threshold = 200;
    if (list.scrollLeft + list.clientWidth < list.scrollWidth - threshold) return;

    const nextPage = wrapper.dataset.nextPage;
    if (!nextPage) return;

    this.recommendationsLoading = true;

    fetch(`${window.location.pathname}?section_id=cart-notification-recommendations&page=${nextPage}`)
      .then((response) => response.text())
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const newWrapper = doc.querySelector('product-recommendations');
        const newItems = newWrapper ? newWrapper.querySelectorAll('[data-cnr-list] > li') : [];

        newItems.forEach((item) => list.appendChild(item));

        if (newWrapper && newWrapper.dataset.nextPage) {
          wrapper.dataset.nextPage = newWrapper.dataset.nextPage;
        } else {
          delete wrapper.dataset.nextPage;
        }

        this.recommendationsLoading = false;
      })
      .catch(() => {
        this.recommendationsLoading = false;
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-recommendations',
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-notification-checkout',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  handleRecommendationAdd(evt) {
    const button = evt.target.closest('[data-cnr-add]');
    if (!button || button.disabled) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.disabled = true;
    button.classList.add('loading');

    const body = JSON.stringify({
      id: variantId,
      quantity: 1,
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.errors) {
          button.disabled = false;
          button.classList.remove('loading');
          return;
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-notification',
          productVariantId: variantId,
          cartData: parsedState,
        });
        this.renderContents(parsedState);
      })
      .catch(() => {
        button.disabled = false;
        button.classList.remove('loading');
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const sectionElement = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return sectionElement ? sectionElement.innerHTML : undefined;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
